import {
  BULK_INVITATION_EMAIL_KEY,
  BulkInvitationColumnSpec,
  BulkInvitationFieldSpec,
  BulkInvitationRowValues,
  createEmptyRow,
  readText,
  spreadsheetFields,
} from "legal-entities/invitations/bulk/bulk-invitation-column-spec";

/**
 * The only module that knows a spreadsheet library exists.
 *
 * SheetJS is imported dynamically at the moment somebody downloads a template or picks a file, so
 * it stays out of the main bundle - it is several times the size of the grid it serves, and most
 * people never open either.
 *
 * It is also the only place that parses something a user supplied. Everything it hands back is
 * plain rows of strings, validated afterwards by the same rules a typed row goes through, so a
 * sheet cannot introduce a value the grid would not have accepted from the keyboard.
 */

/** Cells a `boolean` column accepts as a tick, beyond a real boolean. */
const AFFIRMATIVE_CELLS = new Set(["yes", "y", "true", "t", "x", "1", "✓"]);

/**
 * The top-level domain RFC 2606 reserves for names that can never resolve. The template's example
 * row has an address on it, and a row with such an address is never an invitation, so the example
 * is left out wherever it sits and however it was edited - nobody has to delete it first.
 *
 * Not `example.com`, the obvious choice: that is the domain of every seeded account on the dev
 * stack, the Postman fixtures and the browser tests, so a tester's own rows would disappear.
 */
const EXAMPLE_ADDRESS_TOP_LEVEL_DOMAIN = "invalid";

export const EXAMPLE_ROW_NOTE = "Example row — ignored when you upload";

/** A pale yellow, the fill spreadsheet users already read as "sample" or "note". */
const EXAMPLE_ROW_FILL_ARGB = "FFFFF2CC";

/** Whether an address is on the reserved domain the template's example row uses. */
export function isExampleAddress(email: string): boolean {
  const at = email.lastIndexOf("@");
  if (at < 0) return false;

  // A trailing dot is the fully qualified spelling of the same domain.
  const domain = email.slice(at + 1).trim().toLowerCase().replace(/\.$/, "");
  return domain === EXAMPLE_ADDRESS_TOP_LEVEL_DOMAIN || domain.endsWith(`.${EXAMPLE_ADDRESS_TOP_LEVEL_DOMAIN}`);
}

/** What an uploaded sheet held: the rows to invite, and how many example rows were left out. */
export type ParsedRoster = {
  readonly rows: readonly BulkInvitationRowValues[];
  readonly exampleRowsIgnored: number;
};

export class RosterSheetError extends Error {
  public readonly userFriendlyMessage: string;
  public readonly missingHeaders: readonly string[];

  public constructor(userFriendlyMessage: string, missingHeaders: readonly string[] = []) {
    super(userFriendlyMessage);
    this.name = "RosterSheetError";
    this.userFriendlyMessage = userFriendlyMessage;
    this.missingHeaders = missingHeaders;
  }
}

/**
 * Headings are matched on letters and digits alone, so a template that came back with different
 * capitalisation, a stray space or a trailing colon still lines up. A genuinely different word
 * does not, which is the point: that is a column nobody asked for.
 */
function normalizeHeader(value: unknown): string {
  return String(value ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");
}

function readCell(field: BulkInvitationFieldSpec, cell: unknown): string | boolean {
  if (field.kind === "boolean") {
    if (typeof cell === "boolean") return cell;
    return AFFIRMATIVE_CELLS.has(String(cell ?? "").trim().toLowerCase());
  }
  return String(cell ?? "").trim();
}

function isBlankRow(cells: readonly unknown[]): boolean {
  return cells.every((cell) => String(cell ?? "").trim().length === 0);
}

type SheetJS = typeof import("xlsx");

/** The three calls on SheetJS's bundled zip library used here. It ships typed as `any`. */
type ZipEntry = { content: Uint8Array };
type ZipLibrary = {
  read: (data: Uint8Array, options: { type: "array" }) => unknown;
  find: (zip: unknown, path: string) => ZipEntry | null;
  write: (zip: unknown, options: { fileType: "zip"; type: "array"; compression: boolean }) => ArrayLike<number>;
};

/**
 * Shades the example row, which SheetJS's community build cannot do: it reads cell fills and
 * writes none. So the fill goes into the finished file's XML - one solid fill, one cell format
 * using it, and that format on every cell of row 2.
 *
 * If a SheetJS release writes XML these patterns do not recognise, the template is returned
 * unshaded. The row is still marked by its note and still ignored on upload; a spec asserts the
 * fill, so the loss is caught there rather than by somebody opening the file.
 */
function shadeExampleRow(XLSX: SheetJS, written: ArrayBuffer): ArrayLike<number> {
  const zipLibrary = XLSX.CFB as ZipLibrary;
  const zip = zipLibrary.read(new Uint8Array(written), { type: "array" });
  const stylesEntry = zipLibrary.find(zip, "/xl/styles.xml");
  const sheetEntry = zipLibrary.find(zip, "/xl/worksheets/sheet1.xml");
  if (!stylesEntry || !sheetEntry) return new Uint8Array(written);

  const styles = new TextDecoder().decode(stylesEntry.content);
  const fills = /<fills count="(\d+)">/.exec(styles);
  const formats = /<cellXfs count="(\d+)">/.exec(styles);
  if (!fills || !formats) return new Uint8Array(written);

  const fillIndex = Number(fills[1]);
  const formatIndex = Number(formats[1]);
  const shadedStyles = styles
    .replace(
      /<fills count="\d+">([\s\S]*?)<\/fills>/,
      (_whole, existing: string) =>
        `<fills count="${fillIndex + 1}">${existing}` +
        `<fill><patternFill patternType="solid"><fgColor rgb="${EXAMPLE_ROW_FILL_ARGB}"/><bgColor indexed="64"/></patternFill></fill></fills>`,
    )
    .replace(
      /<cellXfs count="\d+">([\s\S]*?)<\/cellXfs>/,
      (_whole, existing: string) =>
        `<cellXfs count="${formatIndex + 1}">${existing}` +
        `<xf numFmtId="0" fontId="0" fillId="${fillIndex}" borderId="0" xfId="0" applyFill="1"/></cellXfs>`,
    );

  const sheetXml = new TextDecoder().decode(sheetEntry.content);
  const shadedSheet = sheetXml.replace(/<c r="([A-Z]+2)"/g, `<c r="$1" s="${formatIndex}"`);

  stylesEntry.content = new TextEncoder().encode(shadedStyles);
  sheetEntry.content = new TextEncoder().encode(shadedSheet);
  return zipLibrary.write(zip, { fileType: "zip", type: "array", compression: true });
}

/**
 * Builds the sheet somebody fills in, from the same field list that draws the grid. Nothing else
 * generates it, so the template and the grid cannot describe different columns.
 *
 * Row 2 is a complete, fictional entry, shaded and noted as an example. Its address is on the
 * reserved `.invalid` domain, which is what `parseRosterWorkbook` leaves out - so a sheet sent back
 * with the example still in it invites nobody extra.
 */
export async function buildRosterTemplate(spec: BulkInvitationColumnSpec): Promise<Blob> {
  const XLSX = await import("xlsx");
  const fields = spreadsheetFields(spec);

  const examples = fields.map((field) => field.example ?? "");
  const sheet = XLSX.utils.aoa_to_sheet([fields.map((field) => field.header), examples]);
  sheet["!cols"] = fields.map((field, column) => ({
    wch: Math.max(16, field.header.length + 2, examples[column].length + 2),
  }));

  // On the first cell, where somebody starts reading the row. Hidden until hovered, the way
  // spreadsheet notes normally are, so it does not sit over the cells beside it.
  const noteCell = sheet[XLSX.utils.encode_cell({ r: 1, c: 0 })] as { c?: unknown } | undefined;
  if (noteCell) noteCell.c = Object.assign([{ a: "Attorney Hub", t: EXAMPLE_ROW_NOTE }], { hidden: true });

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, sheet, spec.templateSheetName);

  const written = XLSX.write(workbook, { bookType: "xlsx", type: "array" }) as ArrayBuffer;
  return new Blob([new Uint8Array(shadeExampleRow(XLSX, written))], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
}

export async function downloadRosterTemplate(spec: BulkInvitationColumnSpec): Promise<void> {
  const blob = await buildRosterTemplate(spec);
  const url = globalThis.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.setAttribute("href", url);
  link.setAttribute("download", `${spec.templateFileName}.xlsx`);
  link.click();
  globalThis.URL.revokeObjectURL(url);
}

/**
 * Reads a filled-in sheet into rows the grid can hold.
 *
 * A sheet that does not line up is reported by naming the heading that was looked for and not
 * found, never as a parse exception. A misspelled heading is indistinguishable from an absent one
 * - which is exactly why the message says what was expected rather than what was wrong.
 *
 * A row whose address is on the example domain is counted and left out, wherever it sits.
 */
export async function parseRosterWorkbook(
  file: File | Blob,
  spec: BulkInvitationColumnSpec,
): Promise<ParsedRoster> {
  const buffer = await file.arrayBuffer();

  const XLSX = await import("xlsx");
  let grid: unknown[][];
  try {
    const workbook = XLSX.read(buffer, { type: "array" });
    const sheetName = workbook.SheetNames[0];
    const sheet = sheetName ? workbook.Sheets[sheetName] : undefined;
    if (!sheet) throw new Error("no sheet");

    grid = XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1, raw: false, defval: "" });
  } catch {
    throw new RosterSheetError(
      "This file could not be read as a spreadsheet. Download the template and fill that in.",
    );
  }

  const headerRow = grid.find((cells) => Array.isArray(cells) && !isBlankRow(cells));
  if (!headerRow) {
    throw new RosterSheetError("This sheet is empty. Download the template and fill that in.");
  }

  const columnOfHeader = new Map<string, number>();
  headerRow.forEach((cell, column) => {
    const normalized = normalizeHeader(cell);
    if (normalized.length > 0 && !columnOfHeader.has(normalized)) columnOfHeader.set(normalized, column);
  });

  const fields = spreadsheetFields(spec);
  const missing = fields
    .filter((field) => field.required && !columnOfHeader.has(normalizeHeader(field.header)))
    .map((field) => field.header);

  if (missing.length > 0) {
    throw new RosterSheetError(
      `This sheet is missing ${missing.length === 1 ? "a column" : "columns"}: ${missing.join(", ")}. ` +
        "Check the spelling of the headings, or download the template and fill that in.",
      missing,
    );
  }

  const bodyRows = grid.slice(grid.indexOf(headerRow) + 1);
  const rows: BulkInvitationRowValues[] = [];
  let exampleRowsIgnored = 0;

  for (const cells of bodyRows) {
    if (!Array.isArray(cells) || isBlankRow(cells)) continue;

    const row = createEmptyRow(spec);
    for (const field of fields) {
      const column = columnOfHeader.get(normalizeHeader(field.header));
      if (column === undefined) continue;
      row[field.key] = readCell(field, cells[column]);
    }

    if (isExampleAddress(readText(row, BULK_INVITATION_EMAIL_KEY))) {
      exampleRowsIgnored++;
      continue;
    }
    rows.push(row);
  }

  if (rows.length === 0 && exampleRowsIgnored > 0) {
    throw new RosterSheetError(
      "This sheet has only the example row, which is never invited. Add your own rows under it.",
    );
  }
  if (rows.length === 0) {
    throw new RosterSheetError("This sheet has headings but no rows under them.");
  }

  return { rows, exampleRowsIgnored };
}
